import { useEffect, useRef, useState } from "react";
import { errorText, opsApi, type ScannedProduct } from "@/lib/warehouseOps";

/**
 * Ô nhập mã hàng, đi ngay sau bước quét vị trí.
 *
 * Quét đúng ô rồi vẫn có thể với tay nhầm thùng bên cạnh — một ô được phép chứa
 * nhiều mặt hàng và nhiều lô. Mã hàng là thứ duy nhất phân biệt được chúng, nên
 * bước này không có lối bỏ qua.
 *
 * `expected` là danh sách mã hàng mà màn hình đang chờ; quét ra mặt hàng ngoài
 * danh sách thì chặn ngay tại máy, không gửi lên rồi chờ máy chủ từ chối.
 */
export default function ProductScanner({
  token,
  label = "Quét mã hàng",
  hint,
  expected,
  expectedLabel,
  onScanned,
}: {
  token?: string;
  label?: string;
  hint?: string;
  /** Mã hàng được chấp nhận; để trống là chấp nhận mọi mặt hàng tra ra được. */
  expected?: string[];
  /** Câu nhắc khi quét ra mặt hàng ngoài danh sách. */
  expectedLabel?: string;
  onScanned: (product: ScannedProduct) => void;
}) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = async (raw: string) => {
    const value = raw.trim();
    if (!value || busy) return;

    setBusy(true);
    setError("");

    try {
      const res = await opsApi.scanProduct(value, token);
      const product = res.data;

      if (expected && expected.length > 0 && !expected.includes(product.code)) {
        setError(
          `Mã vừa quét là ${product.code} (${product.name}). ${
            expectedLabel ?? "Mặt hàng này không nằm trong danh sách cần thao tác."
          }`,
        );
        setCode("");
        return;
      }

      setCode("");
      onScanned(product);
    } catch (e) {
      setError(errorText(e, "Không đọc được mã hàng."));
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3">
      <label className="block text-xs font-medium text-slate-600 mb-1.5">{label}</label>
      <div className="flex gap-2">
        <input
          ref={inputRef}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") void submit(code); }}
          placeholder="Bắn mã trên thùng hàng..."
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          className="flex-1 min-w-0 px-3 py-3 text-base font-mono border-2 border-slate-200 rounded-lg focus:outline-none focus:border-brand-500"
        />
        <button
          onClick={() => void submit(code)}
          disabled={busy || !code.trim()}
          className="shrink-0 px-4 rounded-lg bg-brand-600 text-white font-medium disabled:bg-slate-300"
        >
          {busy ? "..." : "Tìm"}
        </button>
      </div>
      {hint && !error && <p className="mt-2 text-xs text-slate-500">{hint}</p>}
      {error && <p className="mt-2 text-sm font-medium text-red-600">{error}</p>}
    </div>
  );
}
